import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Check,
  Cpu,
  Info,
  Loader2,
  Menu,
  MoreHorizontal,
  Pencil,
  Pin,
  Plus,
  Search,
  Send,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react';

import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/hooks/useAuth';
import { ToastService } from '@/services/toast.service';
import { aiApi } from '../api/ai.api';
import {
  ChatMessage as ChatMessageType,
  ChatRequest,
  Conversation,
} from '../interfaces/ai.interface';

type Bucket = 'today' | 'yesterday' | 'week' | 'older';

interface UiConversation extends Conversation {
  pinned: boolean;
  bucket: Bucket;
}

const PINNED_KEY = 'tls.ai.pinnedConversations';

function loadPinned(): Set<string> {
  try {
    const raw = localStorage.getItem(PINNED_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
}

function savePinned(pinned: Set<string>) {
  localStorage.setItem(PINNED_KEY, JSON.stringify(Array.from(pinned)));
}

function bucketOf(date: string | Date): Bucket {
  const ts = new Date(date).getTime();
  if (!Number.isFinite(ts)) return 'older';
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const startOfYesterday = startOfToday - 86_400_000;
  const startOfWeek = startOfToday - 6 * 86_400_000;
  if (ts >= startOfToday) return 'today';
  if (ts >= startOfYesterday) return 'yesterday';
  if (ts >= startOfWeek) return 'week';
  return 'older';
}

function relativeTime(date: string | Date, isRo: boolean): string {
  const ts = new Date(date).getTime();
  if (!Number.isFinite(ts)) return '';
  const diff = Date.now() - ts;
  const m = Math.floor(diff / 60_000);
  if (m < 1) return isRo ? 'acum' : 'now';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}${isRo ? 'z' : 'd'}`;
  return `${Math.floor(d / 7)}${isRo ? 's' : 'w'}`;
}

function formatTime(date: string | Date): string {
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  const time = d.toTimeString().slice(0, 5);
  if (sameDay) return time;
  const day = d.getDate().toString().padStart(2, '0');
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  return `${day}.${month} · ${time}`;
}

function previewText(text: string): string {
  return (text || '').replace(/[*_#`>|\-]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 80);
}

const MD_COMPONENTS = {
  p: ({ children }: any) => <p className="ai-md-p">{children}</p>,
  ul: ({ children }: any) => <ul className="ai-md-list">{children}</ul>,
  ol: ({ children }: any) => <ol className="ai-md-list">{children}</ol>,
  table: ({ children }: any) => <table className="ai-md-table">{children}</table>,
  code: ({ inline, children }: any) =>
    inline ? <code className="ai-md-code">{children}</code> : (
      <pre className="ai-md-code" style={{ display: 'block', padding: 10, whiteSpace: 'pre-wrap' }}>
        <code>{children}</code>
      </pre>
    ),
};

export const AiChatPage: React.FC = () => {
  const { t, currentLanguage } = useLanguage();
  const isRo = currentLanguage === 'ro';
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [activeId, setActiveId] = useState<string | null>(null);
  const [optimisticMessages, setOptimisticMessages] = useState<ChatMessageType[]>([]);
  const [pendingTitle, setPendingTitle] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [search, setSearch] = useState('');
  const [menuId, setMenuId] = useState<string | null>(null);
  const [renameTarget, setRenameTarget] = useState<{ id: string; value: string } | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pinned, setPinnedState] = useState<Set<string>>(() => loadPinned());

  const messagesRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    document.body.classList.add('dashboard-claude-bg');
    return () => {
      document.body.classList.remove('dashboard-claude-bg');
    };
  }, []);

  const { data: conversationsRaw = [] } = useQuery({
    queryKey: ['ai-conversations'],
    queryFn: () => aiApi.getConversations(),
  });

  const { data: messagesData, isFetching: messagesLoading } = useQuery({
    queryKey: ['ai-messages', activeId],
    queryFn: () => aiApi.getConversationMessages(activeId!),
    enabled: !!activeId,
  });

  const conversations: UiConversation[] = useMemo(() => {
    return (conversationsRaw || []).map((c) => ({
      ...c,
      pinned: pinned.has(c.uuid),
      bucket: bucketOf(c.lastMessageAt || c.createdAt),
    }));
  }, [conversationsRaw, pinned]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return conversations;
    return conversations.filter((c) => (c.title || '').toLowerCase().includes(q));
  }, [conversations, search]);

  const grouped = useMemo(() => {
    const pinnedList = filtered.filter((c) => c.pinned);
    const rest = filtered.filter((c) => !c.pinned);
    return {
      pinned: pinnedList,
      today: rest.filter((c) => c.bucket === 'today'),
      yesterday: rest.filter((c) => c.bucket === 'yesterday'),
      week: rest.filter((c) => c.bucket === 'week'),
      older: rest.filter((c) => c.bucket === 'older'),
    };
  }, [filtered]);

  const messages: ChatMessageType[] = useMemo(() => {
    const base = messagesData || [];
    if (!activeId) return optimisticMessages;
    return [...base, ...optimisticMessages];
  }, [messagesData, optimisticMessages, activeId]);

  const sendMutation = useMutation({
    mutationFn: (req: ChatRequest) => aiApi.sendMessage(req),
    onMutate: (req) => {
      const optimistic: ChatMessageType = {
        id: Date.now(),
        role: 'user',
        content: req.message,
        status: 'completed',
        createdAt: new Date().toISOString(),
      };
      setOptimisticMessages((prev) => [...prev, optimistic]);
      if (!activeId) {
        setPendingTitle(req.message.slice(0, 48));
      }
    },
    onSuccess: (response) => {
      const assistant: ChatMessageType = {
        id: response.messageId,
        role: 'assistant',
        content: response.content,
        formattedContent: response.formattedContent,
        status: 'completed',
        createdAt: new Date().toISOString(),
      };
      setOptimisticMessages((prev) => [...prev, assistant]);

      if (!activeId) {
        setActiveId(response.conversationId);
      }

      queryClient.invalidateQueries({ queryKey: ['ai-conversations'] });
      queryClient.invalidateQueries({ queryKey: ['ai-messages', response.conversationId] });

      if (response.remainingMessages !== undefined && response.remainingMessages < 10) {
        ToastService.info(
          isRo
            ? `Mai ai ${response.remainingMessages} mesaje disponibile`
            : `${response.remainingMessages} messages left`,
        );
      }
    },
    onError: () => {
      setOptimisticMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 1,
          role: 'assistant',
          content: isRo ? 'A apărut o eroare. Încearcă din nou.' : 'Something went wrong. Try again.',
          status: 'failed',
          createdAt: new Date().toISOString(),
        },
      ]);
    },
    onSettled: () => {
      setPendingTitle(null);
    },
  });

  const renameMutation = useMutation({
    mutationFn: ({ id, title }: { id: string; title: string }) => aiApi.renameConversation(id, title),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-conversations'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => aiApi.deleteConversation(id),
    onSuccess: (_, id) => {
      if (activeId === id) {
        setActiveId(null);
        setOptimisticMessages([]);
      }
      const next = new Set(pinned);
      if (next.delete(id)) {
        savePinned(next);
        setPinnedState(next);
      }
      queryClient.invalidateQueries({ queryKey: ['ai-conversations'] });
    },
  });

  // Reset optimistic messages whenever we switch conversation or backend
  // returns new data for the active conversation.
  useEffect(() => {
    setOptimisticMessages([]);
  }, [activeId]);

  // Auto-scroll on new messages.
  useEffect(() => {
    if (messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  }, [messages.length, activeId, sendMutation.isPending]);

  // Autosize textarea.
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 200)}px`;
  }, [draft]);

  const active = conversations.find((c) => c.uuid === activeId) || null;
  const headerTitle = active
    ? active.title
    : pendingTitle
    ? pendingTitle
    : sendMutation.isPending
    ? t('ai.new')
    : t('ai.title');
  const headerMessageCount = (messagesData?.length || 0) + optimisticMessages.length;

  const newConversation = () => {
    setActiveId(null);
    setOptimisticMessages([]);
    setDraft('');
    setSidebarOpen(false);
  };

  const send = (text?: string) => {
    const content = (text ?? draft).trim();
    if (!content || sendMutation.isPending) return;
    sendMutation.mutate({
      conversationId: activeId || undefined,
      message: content,
    });
    setDraft('');
  };

  const togglePin = (id: string) => {
    const next = new Set(pinned);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    savePinned(next);
    setPinnedState(next);
    setMenuId(null);
  };

  const startRename = (c: UiConversation) => {
    setRenameTarget({ id: c.uuid, value: c.title });
    setMenuId(null);
  };

  const submitRename = () => {
    if (!renameTarget) return;
    const next = renameTarget.value.trim();
    if (!next) {
      setRenameTarget(null);
      return;
    }
    renameMutation.mutate({ id: renameTarget.id, title: next });
    setRenameTarget(null);
  };

  const confirmDelete = (id: string) => {
    setMenuId(null);
    if (!window.confirm(t('ai.delete.confirm'))) return;
    deleteMutation.mutate(id);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !(e.nativeEvent as any).isComposing) {
      e.preventDefault();
      send();
    }
  };

  const userInitials = (user?.fullName || 'TU')
    .split(' ')
    .map((p: string) => p.charAt(0).toUpperCase())
    .join('')
    .slice(0, 2);

  const suggestions = ['ai.s1', 'ai.s2', 'ai.s3', 'ai.s4'];

  return (
    <div className="ai-page">
      <aside className={'ai-sidebar' + (sidebarOpen ? ' open' : '')}>
        <div className="ai-sidebar-head">
          <button className="btn btn-primary ai-new-btn" onClick={newConversation}>
            <Plus size={14} strokeWidth={2.5} />
            <span>{t('ai.new')}</span>
          </button>
          <button
            className="icon-btn ai-sidebar-close"
            onClick={() => setSidebarOpen(false)}
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="ai-search">
          <Search size={14} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('ai.search')}
          />
        </div>

        <div className="ai-conv-list">
          {grouped.pinned.length > 0 && (
            <ConvGroup label={t('ai.pinned')} icon={<Pin size={10} />}>
              {grouped.pinned.map((c) => (
                <ConvItem
                  key={c.uuid}
                  c={c}
                  isRo={isRo}
                  active={c.uuid === activeId}
                  emptyLabel={t('ai.empty_conv')}
                  pinLabel={c.pinned ? t('ai.unpin') : t('ai.pin')}
                  renameLabel={t('ai.rename')}
                  deleteLabel={t('ai.delete')}
                  onClick={() => {
                    setActiveId(c.uuid);
                    setSidebarOpen(false);
                  }}
                  menuOpen={menuId === c.uuid}
                  onOpenMenu={() => setMenuId(menuId === c.uuid ? null : c.uuid)}
                  onCloseMenu={() => setMenuId(null)}
                  onPin={() => togglePin(c.uuid)}
                  onRename={() => startRename(c)}
                  onDelete={() => confirmDelete(c.uuid)}
                />
              ))}
            </ConvGroup>
          )}

          {(['today', 'yesterday', 'week', 'older'] as Bucket[]).map((bucket) => {
            const list = grouped[bucket];
            if (list.length === 0) return null;
            return (
              <ConvGroup key={bucket} label={t(`ai.${bucket}`)}>
                {list.map((c) => (
                  <ConvItem
                    key={c.uuid}
                    c={c}
                    isRo={isRo}
                    active={c.uuid === activeId}
                    emptyLabel={t('ai.empty_conv')}
                    pinLabel={c.pinned ? t('ai.unpin') : t('ai.pin')}
                    renameLabel={t('ai.rename')}
                    deleteLabel={t('ai.delete')}
                    onClick={() => {
                      setActiveId(c.uuid);
                      setSidebarOpen(false);
                    }}
                    menuOpen={menuId === c.uuid}
                    onOpenMenu={() => setMenuId(menuId === c.uuid ? null : c.uuid)}
                    onCloseMenu={() => setMenuId(null)}
                    onPin={() => togglePin(c.uuid)}
                    onRename={() => startRename(c)}
                    onDelete={() => confirmDelete(c.uuid)}
                  />
                ))}
              </ConvGroup>
            );
          })}

          {filtered.length === 0 && (
            <div className="ai-empty-list">
              <Search size={20} />
              <div>{t('common.no_results.simple')}</div>
            </div>
          )}
        </div>

        <div className="ai-sidebar-foot">
          <Info size={12} />
          <span>{t('ai.powered')}</span>
        </div>
      </aside>

      <section className="ai-main">
        <div className="ai-main-head">
          <button
            className="icon-btn ai-sidebar-toggle"
            onClick={() => setSidebarOpen(true)}
            aria-label="Conversations"
          >
            <Menu size={18} />
          </button>
          <div className="ai-main-head-info">
            <div className="ai-main-head-title">{headerTitle}</div>
            <div className="ai-main-head-sub">
              <Cpu size={11} />
              <span>Claude · TLS</span>
              {headerMessageCount > 0 && (
                <>
                  <span className="ai-dot">·</span>
                  <span>
                    {headerMessageCount} {t('ai.messages')}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="ai-messages" ref={messagesRef}>
          {activeId && messagesLoading && messages.length === 0 ? (
            <div className="ai-empty">
              <Loader2 size={20} className="ai-spin" />
            </div>
          ) : messages.length > 0 ? (
            <div className="ai-messages-inner">
              {messages.map((m) => (
                <Message
                  key={m.id}
                  m={m}
                  isRo={isRo}
                  initials={userInitials}
                  youLabel={t('ai.you')}
                  assistantLabel={t('ai.assistant')}
                />
              ))}
              {sendMutation.isPending && (
                <div className="ai-msg ai-msg-assistant">
                  <div className="ai-msg-avatar ai-avatar-bot">
                    <Sparkles size={14} />
                  </div>
                  <div className="ai-msg-body">
                    <div className="ai-msg-meta">{t('ai.assistant')}</div>
                    <div className="ai-typing">
                      <span />
                      <span />
                      <span />
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <AssistantEmpty
              title={t('ai.empty.title')}
              sub={t('ai.empty.sub')}
              suggestions={suggestions.map((k) => t(k))}
              onPick={(q) => send(q)}
            />
          )}
        </div>

        <div className="ai-composer-wrap">
          <div className="ai-composer">
            <textarea
              ref={textareaRef}
              className="ai-textarea"
              placeholder={t('ai.input.placeholder')}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKeyDown}
              rows={1}
              disabled={sendMutation.isPending}
            />
            <button
              className="ai-comp-btn ai-comp-send"
              onClick={() => send()}
              disabled={sendMutation.isPending || !draft.trim()}
              title={t('ai.send')}
            >
              {sendMutation.isPending ? (
                <Loader2 size={14} strokeWidth={2.5} className="ai-spin" />
              ) : (
                <Send size={14} strokeWidth={2.2} />
              )}
            </button>
          </div>
          <div className="ai-composer-hint">
            <Info size={11} />
            <span>{t('ai.shortcut.hint')}</span>
          </div>
        </div>
      </section>

      {sidebarOpen && <div className="ai-sidebar-backdrop" onClick={() => setSidebarOpen(false)} />}

      {renameTarget && (
        <RenameDialog
          value={renameTarget.value}
          title={t('ai.rename.title')}
          subtitle={t('ai.rename.sub')}
          label={t('ai.rename.label')}
          placeholder={t('ai.rename.placeholder')}
          cancelLabel={t('common.cancel')}
          saveLabel={t('ai.rename.save')}
          onChange={(v) => setRenameTarget({ ...renameTarget, value: v })}
          onCancel={() => setRenameTarget(null)}
          onSubmit={submitRename}
        />
      )}
    </div>
  );
};

interface ConvGroupProps {
  label: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}

const ConvGroup: React.FC<ConvGroupProps> = ({ label, icon, children }) => (
  <div className="ai-conv-group">
    <div className="ai-conv-group-label">
      {icon}
      <span>{label}</span>
    </div>
    <div className="ai-conv-group-items">{children}</div>
  </div>
);

interface ConvItemProps {
  c: UiConversation;
  isRo: boolean;
  active: boolean;
  emptyLabel: string;
  pinLabel: string;
  renameLabel: string;
  deleteLabel: string;
  onClick: () => void;
  menuOpen: boolean;
  onOpenMenu: () => void;
  onCloseMenu: () => void;
  onPin: () => void;
  onRename: () => void;
  onDelete: () => void;
}

const ConvItem: React.FC<ConvItemProps> = ({
  c,
  isRo,
  active,
  emptyLabel,
  pinLabel,
  renameLabel,
  deleteLabel,
  onClick,
  menuOpen,
  onOpenMenu,
  onCloseMenu,
  onPin,
  onRename,
  onDelete,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) onCloseMenu();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen, onCloseMenu]);

  const previewSource = c.summary || c.title || '';
  const preview = previewText(previewSource) || emptyLabel;

  return (
    <div className={'ai-conv-item' + (active ? ' active' : '')} onClick={onClick}>
      <div className="ai-conv-body">
        <div className="ai-conv-title">
          {c.pinned && <Pin size={10} fill="currentColor" />}
          <span>{c.title}</span>
        </div>
        <div className="ai-conv-preview">{preview}</div>
      </div>
      <div className="ai-conv-side">
        <span className="ai-conv-time">{relativeTime(c.lastMessageAt || c.createdAt, isRo)}</span>
        <button
          className="ai-conv-more"
          onClick={(e) => {
            e.stopPropagation();
            onOpenMenu();
          }}
          aria-label="More"
        >
          <MoreHorizontal size={14} />
        </button>
        {menuOpen && (
          <div
            ref={menuRef}
            className="ai-conv-menu"
            onClick={(e) => e.stopPropagation()}
          >
            <button className="ai-conv-menu-item" onClick={onPin}>
              <Pin size={13} />
              <span>{pinLabel}</span>
            </button>
            <button className="ai-conv-menu-item" onClick={onRename}>
              <Pencil size={13} />
              <span>{renameLabel}</span>
            </button>
            <div className="dropdown-divider" />
            <button className="ai-conv-menu-item danger" onClick={onDelete}>
              <Trash2 size={13} />
              <span>{deleteLabel}</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

interface MessageProps {
  m: ChatMessageType;
  isRo: boolean;
  initials: string;
  youLabel: string;
  assistantLabel: string;
}

const Message: React.FC<MessageProps> = ({ m, initials, youLabel, assistantLabel }) => {
  const isUser = m.role === 'user';
  return (
    <div className={'ai-msg ' + (isUser ? 'ai-msg-user' : 'ai-msg-assistant')}>
      <div className={'ai-msg-avatar ' + (isUser ? 'ai-avatar-user' : 'ai-avatar-bot')}>
        {isUser ? initials : <Sparkles size={14} />}
      </div>
      <div className="ai-msg-body">
        <div className="ai-msg-meta">
          <span className="ai-msg-author">{isUser ? youLabel : assistantLabel}</span>
          {m.createdAt && <span className="ai-msg-time">{formatTime(m.createdAt)}</span>}
        </div>
        <div className="ai-msg-text">
          {isUser ? (
            <div style={{ whiteSpace: 'pre-wrap' }}>{m.content}</div>
          ) : m.formattedContent ? (
            <div dangerouslySetInnerHTML={{ __html: m.formattedContent }} />
          ) : (
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>
              {m.content || ''}
            </ReactMarkdown>
          )}
        </div>
      </div>
    </div>
  );
};

interface AssistantEmptyProps {
  title: string;
  sub: string;
  suggestions: string[];
  onPick: (q: string) => void;
}

const AssistantEmpty: React.FC<AssistantEmptyProps> = ({ title, sub, suggestions, onPick }) => (
  <div className="ai-empty">
    <div className="ai-empty-icon">
      <Sparkles size={24} strokeWidth={1.6} />
    </div>
    <div className="ai-empty-title">{title}</div>
    <div className="ai-empty-sub">{sub}</div>
    <div className="ai-suggest-grid">
      {suggestions.map((s, i) => (
        <button key={i} className="ai-suggest" onClick={() => onPick(s)}>
          <Sparkles size={13} />
          <span>{s}</span>
        </button>
      ))}
    </div>
  </div>
);

interface RenameDialogProps {
  value: string;
  title: string;
  subtitle: string;
  label: string;
  placeholder: string;
  cancelLabel: string;
  saveLabel: string;
  onChange: (v: string) => void;
  onCancel: () => void;
  onSubmit: () => void;
}

const RenameDialog: React.FC<RenameDialogProps> = ({
  value,
  title,
  subtitle,
  label,
  placeholder,
  cancelLabel,
  saveLabel,
  onChange,
  onCancel,
  onSubmit,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onCancel]);

  return (
    <div
      className="ai-rename-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div className="ai-rename-dialog" role="dialog" aria-modal="true">
        <div className="ai-rename-head">
          <div className="ai-rename-ic">
            <Pencil size={16} />
          </div>
          <div>
            <div className="ai-rename-title">{title}</div>
            <div className="ai-rename-sub">{subtitle}</div>
          </div>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit();
          }}
          className="ai-rename-form"
        >
          <label className="ai-rename-label" htmlFor="ai-rename-input">
            {label}
          </label>
          <input
            id="ai-rename-input"
            ref={inputRef}
            type="text"
            className="ai-rename-input"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            maxLength={80}
            placeholder={placeholder}
          />
          <div className="ai-rename-foot">
            <span className="ai-rename-count">{(value || '').length}/80</span>
            <div className="row gap-2">
              <button type="button" className="btn btn-ghost" onClick={onCancel}>
                {cancelLabel}
              </button>
              <button type="submit" className="btn btn-primary" disabled={!value.trim()}>
                <Check size={13} strokeWidth={2.5} />
                {saveLabel}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AiChatPage;
